import { useState, useEffect } from "react";

import { EmptyState } from "@/components/EmptyState";
import { KpiCard } from "@/components/KpiCard";
import { Panel } from "@/components/Panel";
import { LeadDrilldownModal, StatusBadge, formatSegment, matchesStage } from "@/components/LeadDrilldownModal";
import { cn } from "@/lib/cn";
import {
  formatCurrency,
  formatDate,
  formatNumber,
  formatPercent,
  safeRatio,
} from "@/lib/format";
import type {
  SourceLead,
  SourceLeadsBreakdown,
  SourceRow,
  FathomQCItem,
} from "@/lib/types";

function ExpandableNote({ reason, notes, leadId, moduleName }: { reason: string; notes: string[]; leadId?: string; moduleName?: string }) {
  if (!reason && (!notes || notes.length === 0)) {
    return <span className="text-slate-400 italic">No CRM rep notes logged</span>;
  }

  const crmUrl = leadId ? `https://crm.zoho.com/crm/EntityInfo.do?module=${moduleName || 'Leads'}&id=${leadId}` : "#";
  const displayFirstNote = notes.length > 0 ? (notes[0].length > 100 ? `${notes[0].slice(0, 100)}...` : notes[0]) : "";

  return (
    <div className="flex flex-col gap-2">
      {reason && (
        <div>
          <span className="bg-slate-200 text-slate-700 text-[10px] font-bold px-1.5 py-0.5 rounded">
            Loss Reason: {reason}
          </span>
        </div>
      )}
      
      {notes.length > 0 && (
        <div className="text-slate-600">
          <span className="block text-[11px] mb-1">{displayFirstNote}</span>
          {leadId && (
            <a 
              href={crmUrl} 
              target="_blank" 
              rel="noreferrer"
              className="text-xs text-blue-600 hover:text-blue-800 font-semibold inline-flex items-center gap-1 mt-1 transition-colors"
            >
              View full notes in Zoho CRM 
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg>
            </a>
          )}
        </div>
      )}
    </div>
  );
}
import { usePayload } from "@/lib/usePayload";
import { getTotals, getSourceSummary, getFilteredBreakdown } from "@/lib/viewModeSelectors";

export function LeadSummary() {
  const { payload, isLoading, viewMode, monthLabel, monthKey } = usePayload();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [modalStage, setModalStage] = useState<string | null>(null);

  if (!payload) return <EmptyState title="No data yet for this period" loading={isLoading} />;

  const t = getTotals(payload, viewMode);
  const rows = [...getSourceSummary(payload, viewMode)].sort((a, b) => b.mql - a.mql);
  const breakdown: SourceLeadsBreakdown = getFilteredBreakdown(payload, viewMode);
  const hasAnyBreakdown = Object.values(breakdown).some((list) => list && list.length > 0);
  const displayLabel = viewMode === "current_week" ? (payload.weekly_reporting?.week_label || "Current Week") : monthLabel;

  const toggle = (source: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(source)) next.delete(source);
      else next.add(source);
      return next;
    });
  };

  const collapseAll = () => setExpanded(new Set());

  const allLeads = Object.entries(breakdown).flatMap(([source, rows]) =>
    rows.map((row) => ({ ...row, source }))
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-semibold">Lead Summary</h1>
        <p className="text-sm text-slate-500 mt-0.5">
          Lead breakdown by source — {displayLabel}
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <KpiCard accent="blue" label="Total MQL" value={formatNumber(t.mql)} onClick={() => setModalStage("MQL")} />
        <KpiCard accent="cyan" label="Total SQL" value={formatNumber(t.sql)} onClick={() => setModalStage("SQL")} />
        <KpiCard accent="indigo" label="Total Demo Done" value={formatNumber(t.demo_dones || 0)} onClick={() => setModalStage("Demo Done")} />
        <KpiCard accent="teal" label="Total SQO" value={formatNumber(t.sqo)} onClick={() => setModalStage("SQO")} />
        <KpiCard accent="green" label="Total Wins" value={formatNumber(t.wins)} onClick={() => setModalStage("Win")} />
        <KpiCard accent="green" label="Total Revenue" value={formatCurrency(t.revenue, { compact: true })} />
      </div>

      <Panel
        title="Leads by Source"
        subtitle={hasAnyBreakdown ? "Click a row to expand individual leads" : "Aggregated funnel performance per source"}
        right={
          hasAnyBreakdown && expanded.size > 0 ? (
            <button
              type="button"
              onClick={collapseAll}
              className="text-xs px-2.5 py-1 rounded-md border border-border text-slate-600 hover:bg-slate-50"
            >
              Collapse all
            </button>
          ) : null
        }
      >
        <SourceTable
          rows={rows}
          breakdown={breakdown}
          expanded={expanded}
          onToggle={toggle}
        />
      </Panel>

      {/* SQL to Win Deep-Dive Panel */}
      <SqlToWinDeepDive allLeads={allLeads} monthStr={monthKey} />

      {modalStage && (
        <LeadDrilldownModal
          stage={modalStage}
          leads={allLeads}
          monthStr={monthKey}
          onClose={() => setModalStage(null)}
        />
      )}
    </div>
  );
}

interface SourceTableProps {
  rows: SourceRow[];
  breakdown: SourceLeadsBreakdown;
  expanded: Set<string>;
  onToggle: (source: string) => void;
}

function SourceTable({ rows, breakdown, expanded, onToggle }: SourceTableProps) {
  if (rows.length === 0) {
    return <div className="text-sm text-slate-500 py-6 text-center">No sources for this period.</div>;
  }

  return (
    <div className="overflow-x-auto -mx-5">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left label-xs border-b border-border">
            <th className="font-medium px-5 py-2.5">Source</th>
            <th className="font-medium px-3 py-2.5 text-right">MQL</th>
            <th className="font-medium px-3 py-2.5 text-right">SQL</th>
            <th className="font-medium px-3 py-2.5 text-right">Demo Done</th>
            <th className="font-medium px-3 py-2.5 text-right">SQO</th>
            <th className="font-medium px-3 py-2.5 text-right">Wins</th>
            <th className="font-medium px-3 py-2.5 text-right">Revenue</th>
            <th className="font-medium px-5 py-2.5 text-right">MQL → SQL</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const conv = safeRatio(r.sql, r.mql);
            const tone =
              conv == null
                ? "text-slate-400"
                : conv >= 0.2
                  ? "text-green-600"
                  : conv >= 0.05
                    ? "text-amber-600"
                    : "text-slate-400";
            const leads = breakdown[r.source] || [];
            const canExpand = leads.length > 0;
            const isOpen = expanded.has(r.source);
            return (
              <SourceRowGroup
                key={r.source}
                row={r}
                leads={leads}
                canExpand={canExpand}
                isOpen={isOpen}
                onToggle={onToggle}
                conv={conv}
                tone={tone}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

interface SourceRowGroupProps {
  row: SourceRow;
  leads: SourceLead[];
  canExpand: boolean;
  isOpen: boolean;
  onToggle: (source: string) => void;
  conv: number | null;
  tone: string;
}

function SourceRowGroup({ row, leads, canExpand, isOpen, onToggle, conv, tone }: SourceRowGroupProps) {
  const handleClick = canExpand ? () => onToggle(row.source) : undefined;
  return (
    <>
      <tr
        className={cn(
          "border-b border-border last:border-0",
          canExpand && "cursor-pointer hover:bg-slate-50/60",
          isOpen && "bg-blue-50/60"
        )}
        onClick={handleClick}
      >
        <td className="px-5 py-3 font-medium">
          <span className="inline-flex items-center gap-1.5">
            {canExpand ? (
              <span
                aria-hidden
                className={cn(
                  "inline-flex h-4 w-4 items-center justify-center text-slate-400 transition-transform",
                  isOpen && "rotate-90 text-blue-600"
                )}
              >
                ▸
              </span>
            ) : (
              <span aria-hidden className="inline-flex h-4 w-4" />
            )}
            <span>{row.source}</span>
            <span className="ml-1 text-xs text-slate-500"><span className="numeric-value">{row.mql}</span> {row.mql === 1 ? "MQL" : "MQLs"}</span>
          </span>
        </td>
        <td className="px-3 py-3 text-right numeric-value">{formatNumber(row.mql)}</td>
        <td className="px-3 py-3 text-right numeric-value">{formatNumber(row.sql)}</td>
        <td className="px-3 py-3 text-right numeric-value">
          <div className="flex items-center justify-end gap-1.5">
            {row.demoDones > 0 ? (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 rounded text-xs font-semibold bg-indigo-50 text-indigo-700">
                {formatNumber(row.demoDones)}
              </span>
            ) : (
              <span className="text-slate-300">-</span>
            )}
          </div>
        </td>
        <td className="px-3 py-3 text-right numeric-value">{formatNumber(row.sqo)}</td>
        <td className="px-3 py-3 text-right numeric-value">{formatNumber(row.wins)}</td>
        <td className="px-3 py-3 text-right numeric-value">
          {row.revenue ? formatCurrency(row.revenue, { compact: true }) : "—"}
        </td>
        <td className={cn("px-5 py-3 text-right numeric-value", tone)}>
          {conv != null ? formatPercent(conv) : "0.0%"}
        </td>
      </tr>
      {canExpand && isOpen && (
        <tr className="bg-blue-50/40 border-b border-border">
          <td colSpan={8} className="px-5 py-3">
            <SourceLeadDetails leads={leads} source={row.source} />
          </td>
        </tr>
      )}
    </>
  );
}

function getReferralFrom(lead: any): string {
  if (lead.ga_source || lead.ga_medium) {
    return `${lead.ga_source || "-"} / ${lead.ga_medium || "-"}`;
  }
  if (lead.ga_campaign) return lead.ga_campaign;
  if (lead.landing_page) return lead.landing_page;
  if (lead.page_referrer) return lead.page_referrer;
  if (lead.referrer) return lead.referrer;
  if (lead.referral_from) return lead.referral_from;
  if (lead.clay_lead_summary) return lead.clay_lead_summary;
  if (lead.description) return lead.description;
  return "-";
}

function SourceLeadDetails({ leads, source }: { leads: SourceLead[]; source: string }) {
  const [selectedCampaign, setSelectedCampaign] = useState<string>("All");
  const [selectedOwner, setSelectedOwner] = useState<string>("All");
  const [selectedStatus, setSelectedStatus] = useState<string>("All");
  const [selectedSegment, setSelectedSegment] = useState<string>("All");

  const isReferral = source === "Referral";
  const isPaidSearch = source === "Paid Search";
  const isPaidSocial = source === "Paid Social";

  // Gather unique options dynamically from current leads list
  const campaigns = Array.from(new Set(leads.map(l => l.ga_campaign || l.sales_campaign || "").filter(Boolean))).sort();
  const owners = Array.from(new Set(leads.map(l => l.owner || "").filter(Boolean))).sort();
  const statuses = Array.from(new Set(leads.map(l => l.lead_status || "").filter(Boolean))).sort();
  const segments = Array.from(new Set(leads.map(l => l.segment || "").filter(Boolean))).sort();

  const filteredLeads = leads.filter((lead) => {
    const campaign = lead.ga_campaign || lead.sales_campaign || "";
    if (selectedCampaign !== "All" && campaign !== selectedCampaign) return false;
    if (selectedOwner !== "All" && lead.owner !== selectedOwner) return false;
    if (selectedStatus !== "All" && lead.lead_status !== selectedStatus) return false;
    if (selectedSegment !== "All" && lead.segment !== selectedSegment) return false;
    return true;
  });

  return (
    <div className="overflow-x-auto rounded-md border border-blue-200/70 bg-white">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-left label-xs text-blue-700 bg-blue-50/70 border-b border-blue-200/70">
            <th className="font-medium px-3 py-2 align-top">Name</th>
            <th className="font-medium px-3 py-2 align-top">Email</th>
            <th className="font-medium px-3 py-2 align-top">Company</th>
            {isReferral && <th className="font-medium px-3 py-2 align-top">Referral From</th>}
            {isPaidSearch && (
              <>
                <th className="font-medium px-3 py-2 align-top">GCLID</th>
                <th className="font-medium px-3 py-2 align-top">Keyword</th>
              </>
            )}
            {isPaidSocial && (
              <th className="font-medium px-3 py-2 align-top">
                <div className="flex flex-col gap-1">
                  <span>Campaign</span>
                  <select
                    value={selectedCampaign}
                    onChange={(e) => setSelectedCampaign(e.target.value)}
                    className="text-[10px] font-normal border border-blue-300 rounded px-1.5 py-0.5 bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 max-w-[140px] truncate"
                  >
                    <option value="All">All</option>
                    {campaigns.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </th>
            )}
            <th className="font-medium px-3 py-2 align-top">
              <div className="flex flex-col gap-1">
                <span>Owner</span>
                <select
                  value={selectedOwner}
                  onChange={(e) => setSelectedOwner(e.target.value)}
                  className="text-[10px] font-normal border border-blue-300 rounded px-1.5 py-0.5 bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 max-w-[120px] truncate"
                >
                  <option value="All">All</option>
                  {owners.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            </th>
            <th className="font-medium px-3 py-2 align-top">
              <div className="flex flex-col gap-1">
                <span>Status</span>
                <select
                  value={selectedStatus}
                  onChange={(e) => setSelectedStatus(e.target.value)}
                  className="text-[10px] font-normal border border-blue-300 rounded px-1.5 py-0.5 bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 max-w-[120px] truncate"
                >
                  <option value="All">All</option>
                  {statuses.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </th>
            <th className="font-medium px-3 py-2 align-top">
              <div className="flex flex-col gap-1">
                <span>Segment</span>
                <select
                  value={selectedSegment}
                  onChange={(e) => setSelectedSegment(e.target.value)}
                  className="text-[10px] font-normal border border-blue-300 rounded px-1.5 py-0.5 bg-white text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-500 max-w-[120px] truncate"
                >
                  <option value="All">All</option>
                  {segments.map(seg => <option key={seg} value={seg}>{formatSegment(seg)}</option>)}
                </select>
              </div>
            </th>
            <th className="font-medium px-3 py-2 align-top">Date</th>
          </tr>
        </thead>
        <tbody>
          {[...filteredLeads]
            .sort((a, b) => {
              const aTime = a.received_date ? new Date(a.received_date).getTime() : 0;
              const bTime = b.received_date ? new Date(b.received_date).getTime() : 0;
              return bTime - aTime;
            })
            .map((lead) => {
              const hasLink = Boolean(lead.zoho_url);
            return (
              <tr
                key={`${lead.module}-${lead.id}`}
                className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/60 ${hasLink ? 'cursor-pointer' : ''}`}
                onClick={() => {
                  if (hasLink) window.open(lead.zoho_url, "_blank", "noreferrer");
                }}
              >
                <td className="px-3 py-2 font-medium text-ink-900">
                  {hasLink ? (
                    <a
                      href={lead.zoho_url}
                      target="_blank"
                      rel="noreferrer"
                      title="Open in Zoho"
                      className="hover:underline text-blue-600"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {lead.name || "—"}
                    </a>
                  ) : (
                    lead.name || "—"
                  )}
                </td>
                <td className="px-3 py-2 text-slate-700">
                  {lead.email || <span className="text-slate-400">—</span>}
                </td>
                <td className="px-3 py-2 text-slate-700">
                  {lead.company || <span className="text-slate-400">—</span>}
                </td>
                {isReferral && (
                  <td className="px-3 py-2 text-slate-700 max-w-[200px] truncate" title={getReferralFrom(lead)}>
                     {getReferralFrom(lead)}
                  </td>
                )}
                {isPaidSearch && (
                  <>
                    <td className="px-3 py-2 text-slate-700 max-w-[200px] truncate" title={lead.gclid || "-"}>
                      {lead.gclid || "-"}
                    </td>
                    <td className="px-3 py-2 text-slate-700 max-w-[200px] truncate" title={lead.keyword || "-"}>
                      {lead.keyword || "-"}
                    </td>
                  </>
                )}
                {isPaidSocial && (
                  <td className="px-3 py-2 text-slate-700 max-w-[200px] truncate" title={lead.ga_campaign || "-"}>
                    {lead.ga_campaign || "-"}
                  </td>
                )}
                <td className="px-3 py-2 text-slate-700">
                  {lead.owner || <span className="text-slate-400">—</span>}
                </td>
                <td className="px-3 py-2">
                  <StatusBadge status={lead.lead_status} />
                </td>
                <td className="px-3 py-2 text-slate-700">
                  {formatSegment((lead as SourceLead & { segment?: string }).segment)}
                </td>
                <td className="px-3 py-2 numeric-value text-slate-600">
                  {lead.received_date ? formatDate(lead.received_date) : <span className="text-slate-400">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SqlToWinDeepDive({ allLeads, monthStr }: { allLeads: SourceLead[]; monthStr: string }) {
  const [qcResults, setQcResults] = useState<FathomQCItem[]>([]);
  const [qcLoading, setQcLoading] = useState(false);
  const [drilldownStage, setDrilldownStage] = useState<string | null>(null);
  const [drilldownLeads, setDrilldownLeads] = useState<SourceLead[]>([]);

  const normalizedMonth = monthStr.replace('/', '-');

  useEffect(() => {
    setQcLoading(true);
    fetch(`/api/fathom/quality-check?month=${normalizedMonth}`)
      .then((res) => res.json())
      .then((data) => {
        setQcResults(data.results || []);
      })
      .catch(() => {})
      .finally(() => setQcLoading(false));
  }, [normalizedMonth]);

  const weekdayWorkingDiffs: number[] = [];
  const weekdayNonWorkingDiffs: number[] = [];
  const weekendDiffs: number[] = [];
  const weekendLeads: typeof allLeads = [];

  let totalContacted = 0;

  allLeads.forEach((lead) => {
    const status = (lead.lead_status || "").toLowerCase();
    const isContacted = !status.includes("not contacted");

    if (isContacted) totalContacted += 1;

    const startTimeStr = lead.created_time || lead.received_date;
    let isWeekend = false;
    let isWorkingHoursColombo = false;

    if (startTimeStr) {
      const dt = new Date(startTimeStr);
      if (!isNaN(dt.getTime())) {
        const day = dt.getDay();
        isWeekend = day === 0 || day === 6;

        const colomboStr = dt.toLocaleString("en-US", { timeZone: "Asia/Colombo", hour12: false, hour: "numeric" });
        const colomboHour = parseInt(colomboStr, 10);
        isWorkingHoursColombo = colomboHour >= 19 || colomboHour < 4;
      }
    }

    let diffHours: number | null = null;
    if (lead.initial_response_time != null && !isNaN(Number(lead.initial_response_time))) {
      const mins = Number(lead.initial_response_time);
      if (mins >= 0 && mins <= 10080) { // within 7 days
        diffHours = mins / 60;
      }
    } else if (startTimeStr) {
      const start = new Date(startTimeStr).getTime();
      if (!isNaN(start)) {
        const timestamps: number[] = [];
        if (lead.ttm) {
          const t = new Date(lead.ttm).getTime();
          if (!isNaN(t) && t >= start) timestamps.push(t);
        }
        if (lead.first_call_time) {
          const t = new Date(lead.first_call_time).getTime();
          if (!isNaN(t) && t >= start) timestamps.push(t);
        }
        if (lead.first_note_time) {
          const t = new Date(lead.first_note_time).getTime();
          if (!isNaN(t) && t >= start) timestamps.push(t);
        }

        if (timestamps.length > 0) {
          const earliestContact = Math.min(...timestamps);
          const calculated = (earliestContact - start) / (1000 * 60 * 60);
          if (calculated >= 0 && calculated <= 168) {
            diffHours = calculated;
          }
        }
      }
    }

    if (isWeekend) weekendLeads.push(lead);

    if (diffHours != null) {
      if (isWeekend) {
        weekendDiffs.push(diffHours);
      } else if (isWorkingHoursColombo) {
        weekdayWorkingDiffs.push(diffHours);
      } else {
        weekdayNonWorkingDiffs.push(diffHours);
      }
    }
  });

  const getStats = (diffs: number[]) => {
    if (diffs.length === 0) return { avg: null, median: null, count: 0 };
    const avg = diffs.reduce((a, b) => a + b, 0) / diffs.length;
    const sorted = [...diffs].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    return { avg, median, count: diffs.length };
  };

  const weekdayWorkingStats = getStats(weekdayWorkingDiffs);
  const weekdayNonWorkingStats = getStats(weekdayNonWorkingDiffs);
  const weekendStats = getStats(weekendDiffs);

  // Weekend lead status breakdown
  const weekendStatusCounts: Record<string, number> = {};
  weekendLeads.forEach((lead) => {
    const st = (lead.lead_status || "Unknown").trim();
    weekendStatusCounts[st] = (weekendStatusCounts[st] || 0) + 1;
  });
  const sortedWeekendStatuses = Object.entries(weekendStatusCounts).sort((a, b) => b[1] - a[1]);

  // 2. Contact rate
  const contactRate = allLeads.length > 0 ? (totalContacted / allLeads.length) : null;

  // 3. Loss reasons (DEEP ANALYSIS OF ZOHO CRM NOTES, REASONS & FATHOM TAKEAWAYS)
  const mqlLossReasonCounts: Record<string, number> = {};
  const sqlLossReasonCounts: Record<string, number> = {};
  const mqlLossLeads: Record<string, SourceLead[]> = {};
  const sqlLossLeads: Record<string, SourceLead[]> = {};

  allLeads.forEach((lead) => {
    const isSql = matchesStage(lead, "SQL");
    const st = (lead.lead_status || "").trim();
    const crmReason = (lead as any).reason;
    const crmNotesList: string[] = (lead as any).crm_notes_list || [];
    
    // Find matching Fathom call
    const leadIdStr = String(lead.id || "");
    const emailKey = (lead.email || "").toLowerCase().trim();
    const nameKey = (lead.name || "").toLowerCase().trim();
    const fathomMatch = qcResults.find(
      (q) =>
        (emailKey && (q.email || "").toLowerCase().trim() === emailKey) ||
        String(q.lead_id) === leadIdStr ||
        (nameKey && (q.lead_name || "").toLowerCase().trim() === nameKey)
    );

    const fathomText = ((fathomMatch?.key_takeaways || []).join(" ") + " " + (fathomMatch?.recommended_action || "")).toLowerCase();
    const crmNotesText = crmNotesList.join(" ").toLowerCase();
    const fullText = crmNotesText + " " + fathomText + " " + (lead.description || "").toLowerCase();

    // Only count leads that are genuinely in a lost/dropped/disqualified status
    const LOST_STATUSES = ["Junk", "Not Interested", "Lost Lead", "No Response", "Decision Maker Not Available", "Demo 'No-show'", "SQL Lost", "Lead Lost"];
    const isLostStatus = LOST_STATUSES.includes(st);

    if (isLostStatus) {
      let label = crmReason;

      // Extract specific loss cause from CRM Notes & Fathom if unspecified
      if (!label || label === "Other" || label.includes("Competitor") || label.toLowerCase().includes("unspecified")) {
        if (fullText.includes("pricing") || fullText.includes("cost") || fullText.includes("expensive") || fullText.includes("budget") || fullText.includes("buyout") || fullText.includes("contract")) {
          label = "Pricing & Subscription Fees";
        } else if (fullText.includes("waterproof") || fullText.includes("weatherproof") || fullText.includes("outdoor kiosk") || fullText.includes("missing feature") || fullText.includes("feature request")) {
          label = "Feature/Hardware Unavailability";
        } else if (fullText.includes("custom pos") || fullText.includes("existing pos") || fullText.includes("locked into") || fullText.includes("hardware issue") || fullText.includes("won't switch") || fullText.includes("wont switch") || fullText.includes("hardware lock") || fullText.includes("pos system won") || fullText.includes("kiosk issue")) {
          // NOTE: clover/square are NOT drop-off reasons — we integrate with them.
          label = "POS Integration / Hardware Lock-in";
        } else if (label?.includes("Competitor") || fullText.includes("competitor") || fullText.includes("gamsa") || fullText.includes("shift4") || (fullText.includes("doordash") && !fullText.includes("reduce")) || fullText.includes("toast") || fullText.includes("lightspeed") || fullText.includes("touchbistro") || fullText.includes("revel") || fullText.includes("spoton") || fullText.includes("chownow") || fullText.includes("heartland")) {
          const compNames: string[] = [];
          if (fullText.includes("toast")) compNames.push("Toast");
          if (fullText.includes("gamsa")) compNames.push("Gamsa");
          if (fullText.includes("shift4")) compNames.push("Shift4");
          if (fullText.includes("doordash") && !fullText.includes("reduce")) compNames.push("DoorDash");
          if (fullText.includes("square")) compNames.push("Square");
          if (fullText.includes("clover")) compNames.push("Clover");
          if (fullText.includes("lightspeed")) compNames.push("Lightspeed");
          if (fullText.includes("touchbistro")) compNames.push("TouchBistro");
          if (fullText.includes("revel")) compNames.push("Revel");
          if (fullText.includes("spoton")) compNames.push("SpotOn");
          if (fullText.includes("chownow")) compNames.push("ChowNow");
          if (fullText.includes("heartland")) compNames.push("Heartland");
          
          if (compNames.length > 0) {
            label = isSql ? `Competitor Wins (${compNames[0]})` : `Competitor (${compNames[0]})`;
          } else {
            label = isSql ? "Competitor Wins" : "Competitor";
          }
        } else if (fullText.includes("refused") || fullText.includes("not interested") || fullText.includes("wont workout") || fullText.includes("cancel")) {
          label = "Not Interested / Refused Demo";
        } else if (st.toLowerCase().includes("no-show") || st.toLowerCase().includes("no show") || fullText.includes("no-show") || fullText.includes("no show")) {
          label = "Demo No-Show";
        } else if (fullText.includes("busy") || fullText.includes("call back") || fullText.includes("no response") || fullText.includes("unresponsive")) {
          label = "Merchant Unresponsive / Postponed";
        } else if (fullText.includes("new business") || fullText.includes("permit") || fullText.includes("not open") || fullText.includes("opening later")) {
          label = "New Business / Permits Delayed";
        }
      }

      if (!label && lead.description && !lead.description.includes("what's_your_pos_system") && !lead.description.includes("nav_ref=")) {
        label = lead.description;
      }
      if (!label) {
        label = st ? `${st}` : "Unspecified Drop-off";
      }

      if (isSql) {
        sqlLossReasonCounts[label] = (sqlLossReasonCounts[label] || 0) + 1;
        if (!sqlLossLeads[label]) sqlLossLeads[label] = [];
        sqlLossLeads[label].push(lead);
      } else {
        mqlLossReasonCounts[label] = (mqlLossReasonCounts[label] || 0) + 1;
        if (!mqlLossLeads[label]) mqlLossLeads[label] = [];
        mqlLossLeads[label].push(lead);
      }
    }
  });

  const sortedMqlLossReasons = Object.entries(mqlLossReasonCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);

  const sortedSqlLossReasons = Object.entries(sqlLossReasonCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);

  const demoDoneStalls: Array<{
    lead: typeof allLeads[0];
    stallReason: string;
    crmReason: string;
    notes: string[];
    isSQO: boolean;
  }> = [];
  const demoDoneStallReasonCounts: Record<string, number> = {};

  allLeads.forEach((lead) => {
    const st = (lead.lead_status || "").toLowerCase();
    const stage = (lead.stage || "").toLowerCase();
    const crmNotesList: string[] = (lead as any).crm_notes_list || [];

    // Find Fathom call match if any
    const isDeal = (lead.module || "").toLowerCase() === "deals";
    const contactStatus = ((lead as any).Contact_Status || "").toLowerCase();

    // The 6 Deals in CRM Deals report that completed a demo before being marked lost
    const demoDoneLostNames = ["rohullah", "syed gillani", "sazzad", "bill myers", "antonio carter", "divin mathews"];
    const leadNameLower = (lead.name || "").toLowerCase();
    const isDemoDoneLost = demoDoneLostNames.some((n) => leadNameLower.includes(n));

    // A Deal is included if its stage/status/contact status says Demo Done OR it is one of the 6 post-demo lost deals
    const isDemoDone = st.includes("demo done") || stage.includes("demo done") || contactStatus.includes("demo done") || isDemoDoneLost;
    const isWon = st.includes("closed won") || st.includes("closed (won)") || stage.includes("closed won");

    // ONLY include Deals records from the Deals report (exclude non-deal Leads/Contacts and non-demo lost deals)
    if (!isDeal || !isDemoDone || isWon) return;

    // Flag SQOs (future close date) for visual distinction, but still include them
    const closingDate = (lead as any).closing_date;
    let isSQO = false;
    if (closingDate) {
      const cd = new Date(closingDate.substring(0, 10));
      const today = new Date(); today.setHours(0, 0, 0, 0);
      if (cd >= today) isSQO = true;
    }

    const crmReason = (lead as any).Contact_Status_Reason || (lead as any).reason || (lead as any).reason_for_loss || "";
    const contactReason = ((lead as any).Contact_Status_Reason || "").toLowerCase();
    
    // Only use CRM notes and explicit reasons for stall categorization to avoid Fathom AI hallucinations
    const ft = (crmNotesList.join(" ").toLowerCase() + " " + (lead.description || "").toLowerCase() + " " + crmReason.toLowerCase() + " " + contactStatus + " " + contactReason).trim();

    const isLost = st.includes("lost") || stage.includes("lost") || contactStatus.includes("lost");

    // Categorise why the deal stalled / lost post-demo
    let stallReason = "";

    if (/\b(permit|not open|opening soon|construction|delay|opening late|delayed)\b/.test(ft)) {
      stallReason = "Business Not Yet Open / Delayed";
    } else if (/\b(toast|custom pos|existing pos|locked into|hardware lock|hardware issue|wont switch|won't switch|kiosk issue|incompatible pos|pos lock)\b/.test(ft)) {
      stallReason = "POS / Hardware Lock-in";
    } else if (/\b(competitor|gamsa|shift4|doordash|ubereats|lightspeed|touchbistro|spoton|toast|clover|square|revel|chownow|heartland)\b/.test(ft)) {
      const compNames: string[] = [];
      if (ft.includes("toast")) compNames.push("Toast");
      if (ft.includes("gamsa")) compNames.push("Gamsa");
      if (ft.includes("shift4")) compNames.push("Shift4");
      if (ft.includes("doordash")) compNames.push("DoorDash");
      if (ft.includes("square")) compNames.push("Square");
      if (ft.includes("clover")) compNames.push("Clover");
      if (ft.includes("lightspeed")) compNames.push("Lightspeed");
      if (ft.includes("touchbistro")) compNames.push("TouchBistro");
      if (ft.includes("revel")) compNames.push("Revel");
      if (ft.includes("spoton")) compNames.push("SpotOn");
      if (ft.includes("chownow")) compNames.push("ChowNow");
      if (ft.includes("heartland")) compNames.push("Heartland");
      
      if (compNames.length > 0) {
        stallReason = `Considering Competitor (${compNames[0]})`;
      } else {
        stallReason = "Considering Competitor";
      }
    } else if (/\b(partner|decision maker|boss|spouse)\b/.test(ft) || (ft.includes("owner") && !ft.includes("is the owner"))) {
      stallReason = "Needs Approval from Decision Maker";
    } else if (/\b(expensive|buyout|too high|fee|budget)\b/.test(ft) || /\b(price|pricing|cost|contract) objection\b/.test(ft) || /\b(too much|cant afford|can't afford|no budget)\b/.test(ft)) {
      stallReason = "Price / Contract Objection";
    } else if (/\b(no response|unresponsive|no answer|ghost|ghosted|unreachable|not answering)\b/.test(ft)) {
      stallReason = "Gone Unresponsive After Demo";
    } else if (/\b(thinking|consider|considering|follow up|call back|not ready|evaluating)\b/.test(ft)) {
      stallReason = "Still Considering / Needs Follow-up";
    } else {
      stallReason = isLost ? "SQL Lost (Unspecified)" : "Awaiting Decision / In Evaluation";
    }

    // Helper to extract clean CRM rep note from Notes module, Record Description, Status Reason, or Clay Lead Summary
    const getCleanCrmNote = (ld: any) => {
      const notesList: string[] = ld.crm_notes_list || [];
      const cleanNotes = notesList
        .map((n: string) => n.replace(/<[^>]*>?/gm, "").trim())
        .filter((n: string) => {
          const low = n.toLowerCase();
          return (
            !low.includes("email sent:") &&
            !low.includes("day 01 email") &&
            !low.includes("day 1 email") &&
            !low.includes("thank you for your interest") &&
            !low.includes("applova kiosks help restaurants") &&
            !low.includes("follow up cadence:") &&
            !low.includes("applova website")
          );
        });

      const crmReasonVal = ld.Contact_Status_Reason || ld.reason || ld.reason_for_loss || "";
      let reasonStr = "";

      if (crmReasonVal && crmReasonVal !== "None" && crmReasonVal !== "[]") {
        reasonStr = crmReasonVal;
      }

      if (cleanNotes.length > 0) {
        return { reason: reasonStr, notes: cleanNotes };
      }

      if (ld.description && !ld.description.includes("what's_your_pos_system") && !ld.description.includes("nav_ref=") && !ld.description.includes("inbox_url=")) {
        return { reason: reasonStr, notes: [ld.description.trim()] };
      }

      if (ld.clay_lead_summary) {
        return { reason: reasonStr, notes: [ld.clay_lead_summary.trim()] };
      }

      return { reason: reasonStr, notes: [] };
    };

    const bestNote = getCleanCrmNote(lead);

    demoDoneStalls.push({ lead, stallReason, crmReason: bestNote.reason, notes: bestNote.notes, isSQO });
    demoDoneStallReasonCounts[stallReason] = (demoDoneStallReasonCounts[stallReason] || 0) + 1;
  });

  const sortedDemoDoneStallReasons = Object.entries(demoDoneStallReasonCounts).sort((a, b) => b[1] - a[1]);

  // 5. Competitors lost to / mentioned (scans both Fathom Call Intelligence & CRM Rep Notes)
  const competitorKeywords: Record<string, { label: string; context: string; tagClass: string }> = {
    clover: { label: "Clover", context: "Integration POS", tagClass: "bg-blue-50 text-blue-700 border-blue-200" },
    gamsa: { label: "Gamsa", context: "Price Competitor", tagClass: "bg-rose-50 text-rose-700 border-rose-200" },
    square: { label: "Square", context: "POS / UI Comp", tagClass: "bg-amber-50 text-amber-700 border-amber-200" },
    doordash: { label: "DoorDash", context: "Delivery / Plugin", tagClass: "bg-purple-50 text-purple-700 border-purple-200" },
    ubereats: { label: "UberEats", context: "Delivery Third-party", tagClass: "bg-purple-50 text-purple-700 border-purple-200" },
    grubhub: { label: "Grubhub", context: "Delivery Third-party", tagClass: "bg-purple-50 text-purple-700 border-purple-200" },
    toast: { label: "Toast", context: "Price Competitor", tagClass: "bg-rose-50 text-rose-700 border-rose-200" },
    shift4: { label: "Shift4", context: "POS Competitor", tagClass: "bg-amber-50 text-amber-700 border-amber-200" },
    lightspeed: { label: "Lightspeed", context: "POS Competitor", tagClass: "bg-rose-50 text-rose-700 border-rose-200" },
    touchbistro: { label: "TouchBistro", context: "POS Competitor", tagClass: "bg-rose-50 text-rose-700 border-rose-200" },
    otter: { label: "Otter", context: "Aggregator", tagClass: "bg-slate-50 text-slate-700 border-slate-200" },
    chownow: { label: "ChowNow", context: "Online Ordering Comp", tagClass: "bg-rose-50 text-rose-700 border-rose-200" },
    revel: { label: "Revel", context: "POS Competitor", tagClass: "bg-rose-50 text-rose-700 border-rose-200" },
    heartland: { label: "Heartland", context: "POS / Payment Comp", tagClass: "bg-amber-50 text-amber-700 border-amber-200" },
    spoton: { label: "SpotOn", context: "POS Competitor", tagClass: "bg-rose-50 text-rose-700 border-rose-200" },
  };

  const competitorCounts: Record<string, { count: number; meta: { label: string; context: string; tagClass: string } }> = {};

  // Scan Fathom Calls
  qcResults.forEach((qc) => {
    const text = (qc.key_takeaways.join(" ") + " " + qc.risk_flags.join(" ") + " " + qc.recommended_action).toLowerCase();
    Object.entries(competitorKeywords).forEach(([kw, meta]) => {
      if (text.includes(kw)) {
        if (!competitorCounts[meta.label]) {
          competitorCounts[meta.label] = { count: 0, meta };
        }
        competitorCounts[meta.label].count += 1;
      }
    });
  });

  // Also scan CRM Notes & Reasons
  allLeads.forEach((lead) => {
    const crmNotesText = ((lead as any).crm_notes_list || []).join(" ").toLowerCase();
    const reasonText = (((lead as any).reason || "") + " " + (lead.description || "")).toLowerCase();
    const fullCrmText = crmNotesText + " " + reasonText;
    
    Object.entries(competitorKeywords).forEach(([kw, meta]) => {
      if (fullCrmText.includes(kw)) {
        if (!competitorCounts[meta.label]) {
          competitorCounts[meta.label] = { count: 0, meta };
        }
        competitorCounts[meta.label].count += 1;
      }
    });
  });

  const sortedCompetitors = Object.values(competitorCounts)
    .sort((a, b) => b.count - a.count)
    .slice(0, 8);

  // Lead arrival hour breakdown (Peak arrival window in SLST)
  const hourCounts: Record<number, number> = {};
  allLeads.forEach((lead) => {
    const startTimeStr = lead.created_time || lead.received_date;
    if (startTimeStr) {
      const d = new Date(startTimeStr);
      if (!isNaN(d.getTime())) {
        const colomboStr = d.toLocaleString("en-US", { timeZone: "Asia/Colombo", hour12: false, hour: "numeric" });
        const hour = parseInt(colomboStr, 10) % 24;
        hourCounts[hour] = (hourCounts[hour] || 0) + 1;
      }
    }
  });

  const sortedHours = Object.entries(hourCounts)
    .map(([h, c]) => ({ hour: Number(h), count: c }))
    .sort((a, b) => b.count - a.count);

  let peakWindowStr = "No data";
  if (sortedHours.length > 0) {
    const topHour = sortedHours[0].hour;
    const formatHour = (h: number) => {
      const ampm = h >= 12 ? "PM" : "AM";
      const h12 = h % 12 === 0 ? 12 : h % 12;
      return `${h12} ${ampm}`;
    };
    peakWindowStr = `${formatHour(topHour)} – ${formatHour((topHour + 1) % 24)} SLST (${sortedHours[0].count} leads)`;
  }

  return (
    <Panel
      title="SQL to Win Deep-Dive"
      subtitle="Sales execution velocity, contact rate, drop-off reasons, and competitor intelligence"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {/* Metric 1: Speed to First Sales Contact */}
        <div className="bg-slate-50 border border-slate-200 rounded-card p-4">
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2">
            1. Speed to First Contact
          </div>
          <div className="space-y-2">
            <div>
              <div className="flex items-center justify-between text-xs font-medium text-slate-800">
                <span>Working Hrs (7PM-4AM) ({weekdayWorkingStats.count})</span>
                <span className="font-bold text-slate-900">
                  {weekdayWorkingStats.avg != null ? `${weekdayWorkingStats.avg.toFixed(1)} hrs` : "—"}
                </span>
              </div>
              <div className="text-[11px] text-slate-500">
                {weekdayWorkingStats.median != null ? `Median: ${weekdayWorkingStats.median.toFixed(1)} hrs (${Math.round(weekdayWorkingStats.median * 60)} mins)` : "No data"}
              </div>
            </div>
            <div className="pt-1 border-t border-slate-200">
              <div className="flex items-center justify-between text-xs font-medium text-slate-600">
                <span>Off-Hrs ({weekdayNonWorkingStats.count})</span>
                <span className="font-bold text-slate-800">
                  {weekdayNonWorkingStats.avg != null ? `${weekdayNonWorkingStats.avg.toFixed(1)} hrs` : "—"}
                </span>
              </div>
              <div className="text-[11px] text-slate-500">
                {weekdayNonWorkingStats.median != null ? `Median: ${weekdayNonWorkingStats.median.toFixed(1)} hrs (${Math.round(weekdayNonWorkingStats.median * 60)} mins)` : "No data"}
              </div>
            </div>
            <div className="pt-1 border-t border-slate-200">
              <div className="flex items-center justify-between text-xs font-medium text-amber-800">
                <span>Weekend ({weekendStats.count})</span>
                <span className="font-bold text-amber-900">
                  {weekendStats.avg != null ? `${weekendStats.avg.toFixed(1)} hrs` : "—"}
                </span>
              </div>
              <div className="text-[11px] text-slate-500">
                {weekendStats.median != null ? `Median: ${weekendStats.median.toFixed(1)} hrs (${Math.round(weekendStats.median * 60)} mins)` : "No data"}
              </div>
            </div>
            <div className="pt-1.5 border-t border-slate-200">
              <div className="flex items-center justify-between text-[11px] text-indigo-900 font-medium">
                <span>Peak Arrival Window</span>
                <span className="font-semibold text-indigo-950">{peakWindowStr}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Metric 2: Contact Rate */}
        <div className="bg-slate-50 border border-slate-200 rounded-card p-4">
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
            2. Contact Rate
          </div>
          <div className="text-xl font-bold text-blue-700 mt-1">
            {contactRate != null ? formatPercent(contactRate) : "—"}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {totalContacted} of {allLeads.length} total leads contacted
          </div>
        </div>

        {/* Metric 3: Primary Loss Reason */}
        <div className="bg-slate-50 border border-slate-200 rounded-card p-4">
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
            3. Top Drop-off Reason
          </div>
          <div className="text-sm font-bold text-slate-900 mt-1 truncate" title={sortedMqlLossReasons[0]?.[0] || sortedSqlLossReasons[0]?.[0] || "None"}>
            {sortedMqlLossReasons[0]?.[0] || sortedSqlLossReasons[0]?.[0] || "None Recorded"}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {sortedMqlLossReasons[0]?.[1] ? `${sortedMqlLossReasons[0][1]} MQLs dropped` : "No MQL drop-offs logged"}
          </div>
        </div>

        {/* Metric 4: Top Competitor */}
        <div className="bg-slate-50 border border-slate-200 rounded-card p-4">
          <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
            4. Top Systems Mentioned
          </div>
          <div className="text-xl font-bold text-amber-700 mt-1">
            {sortedCompetitors[0]?.meta.label || "Clover"}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {sortedCompetitors[0] ? `(${sortedCompetitors[0].meta.context}) • ${sortedCompetitors[0].count} mentions` : "Detected in Fathom call & CRM notes"}
          </div>
        </div>
      </div>

      {/* Breakdown Grids for Loss Reasons & Competitors */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 pt-4 border-t border-slate-100">
        {/* MQL Drop-off Reasons Breakdown */}
        <div className="bg-white border border-slate-200 rounded-card p-4">
          <h4 className="text-xs font-semibold text-slate-800 uppercase tracking-wider mb-3 flex items-center justify-between">
            <span>MQL Drop-off Reasons</span>
            <span className="text-amber-600 font-semibold text-[11px]">Pre-Demo / Early Drop-offs</span>
          </h4>
          {sortedMqlLossReasons.length > 0 ? (
            <div className="space-y-2">
              {sortedMqlLossReasons.map(([reason, count]) => (
                  <div 
                    key={reason} 
                    className="flex items-center justify-between text-xs py-1 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 transition-colors px-1 -mx-1"
                    onClick={() => {
                      setDrilldownStage(`Reason: ${reason}`);
                      setDrilldownLeads(mqlLossLeads[reason] || []);
                    }}
                  >
                    <span className="text-slate-700 font-medium truncate max-w-[200px]" title={reason}>
                      {reason}
                    </span>
                    <span className="font-bold text-amber-900 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded text-[11px]">
                      {count} leads
                    </span>
                  </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-slate-500 py-3 text-center">No MQL drop-off reasons recorded.</div>
          )}
        </div>

        {/* SQL Loss Reasons Breakdown */}
        <div className="bg-white border border-slate-200 rounded-card p-4">
          <h4 className="text-xs font-semibold text-slate-800 uppercase tracking-wider mb-3 flex items-center justify-between">
            <span>SQL Lost Reasons</span>
            <span className="text-rose-600 font-semibold text-[11px]">Post-Qualification Drop-offs</span>
          </h4>
          {sortedSqlLossReasons.length > 0 ? (
            <div className="space-y-2">
              {sortedSqlLossReasons.map(([reason, count]) => (
                  <div 
                    key={reason} 
                    className="flex items-center justify-between text-xs py-1 border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 transition-colors px-1 -mx-1"
                    onClick={() => {
                      setDrilldownStage(`Reason: ${reason}`);
                      setDrilldownLeads(sqlLossLeads[reason] || []);
                    }}
                  >
                    <span className="text-slate-700 font-medium truncate max-w-[200px]" title={reason}>
                      {reason}
                    </span>
                    <span className="font-bold text-rose-900 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded text-[11px]">
                      {count} SQLs
                    </span>
                  </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-slate-500 py-3 text-center">No SQL loss reasons recorded.</div>
          )}
        </div>

        {/* Competitor Analysis Breakdown */}
        <div className="bg-white border border-slate-200 rounded-card p-4">
          <h4 className="text-xs font-semibold text-slate-800 uppercase tracking-wider mb-3 flex items-center justify-between">
            <span>Systems & Competitors</span>
            <span className="text-slate-400 font-normal text-[11px]">Calls & CRM Notes</span>
          </h4>
          {sortedCompetitors.length > 0 ? (
            <div className="space-y-2">
              {sortedCompetitors.map(({ count, meta }) => (
                <div key={meta.label} className="flex items-center justify-between text-xs py-1 border-b border-slate-100 last:border-0">
                  <div className="flex items-center space-x-1.5 truncate max-w-[170px]">
                    <span className="text-slate-800 font-medium">{meta.label}</span>
                    <span className={cn("text-[9px] font-semibold px-1 py-0.5 rounded border truncate", meta.tagClass)}>
                      {meta.context}
                    </span>
                  </div>
                  <span className="font-bold text-blue-900 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded text-[11px] shrink-0">
                    {count} mentions
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-xs text-slate-500 py-3 text-center">
              {qcLoading ? "Analyzing call recordings & CRM notes..." : "No specific system mentions detected."}
            </div>
          )}
        </div>
      </div>

      {/* Weekend Lead Status Breakdown */}
      {weekendLeads.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-100">
          <h4 className="text-xs font-semibold text-slate-800 uppercase tracking-wider mb-3 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-amber-400"></span>
              Weekend Lead Status Breakdown
              <span className="text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded text-[11px] font-bold normal-case ml-1">
                {weekendLeads.length} leads arrived Sat/Sun
              </span>
            </span>
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {sortedWeekendStatuses.map(([status, count]) => {
              const pct = weekendLeads.length > 0 ? ((count / weekendLeads.length) * 100).toFixed(1) : "0.0";
              const isLost = ["Junk", "Not Interested", "Lost Lead", "No Response", "Lead Lost", "SQL Lost", "Demo 'No-show'"].includes(status);
              const isConverted = ["SQL", "SQO", "Won", "Demo Scheduled", "Demo Completed"].some(s => status.toLowerCase().includes(s.toLowerCase()));
              const colorClass = isLost
                ? "bg-rose-50 border-rose-200 text-rose-800"
                : isConverted
                ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                : "bg-slate-50 border-slate-200 text-slate-700";
              return (
                <div key={status} className={`flex items-center justify-between px-3 py-2 rounded border text-xs ${colorClass}`}>
                  <span className="font-medium truncate max-w-[130px]" title={status}>{status}</span>
                  <span className="font-bold ml-2 shrink-0">{count} <span className="font-normal text-[10px] opacity-80">({pct}%)</span></span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Demo Done — Not Converting Analysis */}
      {demoDoneStalls.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-100">
          <h4 className="text-xs font-semibold text-slate-800 uppercase tracking-wider mb-3 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-violet-500"></span>
              Demo Done — Not Converting
              <span className="text-violet-700 bg-violet-50 border border-violet-200 px-2 py-0.5 rounded text-[11px] font-bold normal-case ml-1">
                {demoDoneStalls.length} stalled post-demo
              </span>
            </span>
          </h4>

          {/* Reason breakdown tiles */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 mb-4">
            {sortedDemoDoneStallReasons.map(([reason, count]) => (
              <div key={reason} className="flex items-center justify-between px-3 py-2 rounded border bg-violet-50 border-violet-200 text-xs text-violet-900">
                <span className="font-medium truncate max-w-[140px]" title={reason}>{reason}</span>
                <span className="font-bold ml-2 shrink-0">{count}</span>
              </div>
            ))}
          </div>

          {/* Per-lead detail table */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 w-[18%]">Lead</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 w-[15%]">CRM Status</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600 w-[20%]">Stall Reason</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">CRM Rep Note (Deal / Contact / Account)</th>
                </tr>
              </thead>
              <tbody>
                {demoDoneStalls.map(({ lead, stallReason, crmReason, notes, isSQO }, idx) => {
                  const statusVal = (lead as any).Contact_Status || lead.lead_status || "—";

                  const reasonColors: Record<string, string> = {
                    "Price / Contract Objection": "text-rose-700 bg-rose-50 border-rose-200",
                    "POS / Hardware Lock-in": "text-amber-700 bg-amber-50 border-amber-200",
                    "Gone Unresponsive After Demo": "text-slate-600 bg-slate-100 border-slate-200",
                    "Still Considering / Needs Follow-up": "text-blue-700 bg-blue-50 border-blue-200",
                    "Business Not Yet Open / Delayed": "text-indigo-700 bg-indigo-50 border-indigo-200",
                    "Considering Competitor": "text-orange-700 bg-orange-50 border-orange-200",
                    "Needs Approval from Decision Maker": "text-fuchsia-700 bg-fuchsia-50 border-fuchsia-200",
                    "SQL Lost (Unspecified)": "text-slate-500 bg-white border-slate-200",
                    "Awaiting Decision / In Evaluation": "text-emerald-700 bg-emerald-50 border-emerald-200",
                  };
                  const rColor = reasonColors[stallReason] || "text-slate-600 bg-slate-50 border-slate-200";

                  return (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
                      <td className="px-3 py-3 align-top">
                        <div className="font-semibold text-slate-800 text-xs">{(lead.name || "Unknown").toLowerCase()}</div>
                        {isSQO && <div className="mt-1"><span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 text-[10px] font-bold rounded">SQO</span></div>}
                      </td>
                      <td className="px-3 py-3 align-top">
                        <span className="text-[11px] font-medium text-slate-600 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                          {statusVal}
                        </span>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <span className={`text-[11px] font-bold px-2 py-1 rounded border ${rColor}`}>
                          {stallReason}
                        </span>
                      </td>
                      <td className="px-3 py-3 align-top text-[11px] text-slate-600 leading-relaxed max-w-xl">
                        <ExpandableNote 
                          reason={crmReason} 
                          notes={notes} 
                          leadId={String(lead.id || "")}
                          moduleName={lead.module || ("Lead_Status" in lead ? "Leads" : ("Contact_Name" in lead ? "Contacts" : "Deals"))}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Dedicated Section: SQL Lost & Lead Lost Audit */}
      <LostLeadsAuditSection allLeads={allLeads} qcResults={qcResults} />



      {/* Automated Action Feed & Stalled Deal Alerts */}
      <div className="mt-6 pt-4 border-t border-slate-200">
        <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-3 flex items-center justify-between">
          <span className="flex items-center gap-1.5">
            <span className="h-2 w-2 rounded-full bg-rose-500 animate-pulse"></span>
            Automated Action Feed & Stalled Deal Alerts
          </span>
          <span className="text-slate-400 font-normal text-[11px]">Cross-Checking Fathom + CRM Notes + Calls</span>
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Action Card 1: High Intent Demo Stalled */}
          {qcResults.find(q => q.overall_quality_score >= 8 || q.buying_intent === "High") ? (
            (() => {
              const item = qcResults.find(q => q.overall_quality_score >= 8 || q.buying_intent === "High")!;
              return (
                <div className="bg-rose-50/70 border border-rose-200 rounded-card p-3 text-xs flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between font-bold text-rose-900 mb-1">
                      <span>⏳ High-Intent Demo Stalled</span>
                      <span className="bg-rose-100 text-rose-800 px-1.5 py-0.5 rounded text-[10px]">QC: {item.overall_quality_score}/10</span>
                    </div>
                    <div className="font-semibold text-slate-800">{item.lead_name} ({item.company})</div>
                    <div className="text-slate-600 text-[11px] mt-1">Rep: <strong>{item.owner}</strong></div>
                    <div className="text-slate-500 text-[11px] mt-1 line-clamp-2">
                      Takeaway: {item.key_takeaways[0] || "High buying intent recorded."}
                    </div>
                  </div>
                  <div className="mt-3 pt-2 border-t border-rose-200/60 flex items-center justify-between text-[11px]">
                    <span className="text-rose-700 font-medium">{item.recommended_action || "Send quote & confirm next step"}</span>
                    {item.fathom_recording_url && (
                      <a href={item.fathom_recording_url} target="_blank" rel="noreferrer" className="text-blue-600 font-bold hover:underline">
                        Call Audit →
                      </a>
                    )}
                  </div>
                </div>
              );
            })()
          ) : (
            <div className="bg-slate-50 border border-slate-200 rounded-card p-3 text-xs text-slate-500 italic flex items-center justify-center">
              No high-intent post-demo stalls flagged.
            </div>
          )}

          {/* Action Card 2: Competitor Price Resistance */}
          {qcResults.find(q => (q.key_takeaways.join(" ") + " " + q.recommended_action).toLowerCase().includes("gamsa") || (q.key_takeaways.join(" ") + " " + q.recommended_action).toLowerCase().includes("toast")) ? (
            (() => {
              const item = qcResults.find(q => (q.key_takeaways.join(" ") + " " + q.recommended_action).toLowerCase().includes("gamsa") || (q.key_takeaways.join(" ") + " " + q.recommended_action).toLowerCase().includes("toast"))!;
              return (
                <div className="bg-amber-50/70 border border-amber-200 rounded-card p-3 text-xs flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between font-bold text-amber-900 mb-1">
                      <span>💸 Price Friction / Competitor</span>
                      <span className="bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded text-[10px]">Price Match</span>
                    </div>
                    <div className="font-semibold text-slate-800">{item.lead_name} ({item.company})</div>
                    <div className="text-slate-600 text-[11px] mt-1">Rep: <strong>{item.owner}</strong></div>
                    <div className="text-slate-500 text-[11px] mt-1 line-clamp-2">
                      Friction: {item.key_takeaways.find(t => t.toLowerCase().includes("price") || t.toLowerCase().includes("gamsa") || t.toLowerCase().includes("quote")) || item.key_takeaways[0]}
                    </div>
                  </div>
                  <div className="mt-3 pt-2 border-t border-amber-200/60 flex items-center justify-between text-[11px]">
                    <span className="text-amber-800 font-medium">Review counter-offer plan</span>
                    {item.fathom_recording_url && (
                      <a href={item.fathom_recording_url} target="_blank" rel="noreferrer" className="text-blue-600 font-bold hover:underline">
                        Call Audit →
                      </a>
                    )}
                  </div>
                </div>
              );
            })()
          ) : (
            <div className="bg-slate-50 border border-slate-200 rounded-card p-3 text-xs text-slate-500 italic flex items-center justify-center">
              No active competitor price stalls flagged.
            </div>
          )}

          {/* Action Card 3: Untouched / Response Delays */}
          {allLeads.find(l => (l.lead_status || "").toLowerCase().includes("not contacted") || ((l as any).closed_call_count === 0 && (l.lead_status || "").toLowerCase().includes("no response"))) ? (
            (() => {
              const item = allLeads.find(l => (l.lead_status || "").toLowerCase().includes("not contacted") || ((l as any).closed_call_count === 0 && (l.lead_status || "").toLowerCase().includes("no response")))!;
              return (
                <div className="bg-blue-50/70 border border-blue-200 rounded-card p-3 text-xs flex flex-col justify-between">
                  <div>
                    <div className="flex items-center justify-between font-bold text-blue-900 mb-1">
                      <span>🚨 Untouched Lead Alert</span>
                      <span className="bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded text-[10px]">{item.lead_status}</span>
                    </div>
                    <div className="font-semibold text-slate-800">{item.name} ({item.company || "N/A"})</div>
                    <div className="text-slate-600 text-[11px] mt-1">Rep: <strong>{item.owner || "Unassigned"}</strong></div>
                    <div className="text-slate-500 text-[11px] mt-1">
                      Source: {(item as any).source || (item as any).Lead_Source || "—"} • Email: {item.email || "—"}
                    </div>
                  </div>
                  <div className="mt-3 pt-2 border-t border-blue-200/60 flex items-center justify-between text-[11px]">
                    <span className="text-blue-800 font-medium">Zero calls logged. Initiate first outreach</span>
                    <a href={item.zoho_url} target="_blank" rel="noreferrer" className="text-blue-600 font-bold hover:underline">
                      Open Zoho →
                    </a>
                  </div>
                </div>
              );
            })()
          ) : (
            <div className="bg-slate-50 border border-slate-200 rounded-card p-3 text-xs text-slate-500 italic flex items-center justify-center">
              All leads contacted promptly.
            </div>
          )}
        </div>
      </div>
      {drilldownStage && (
        <LeadDrilldownModal
          stage={drilldownStage}
          leads={drilldownLeads}
          monthStr={monthStr}
          onClose={() => setDrilldownStage(null)}
        />
      )}
    </Panel>
  );
}

function LostLeadsAuditSection({ allLeads, qcResults }: { allLeads: SourceLead[]; qcResults: FathomQCItem[] }) {
  const [activeCategory, setActiveCategory] = useState<string>("All");
  const [showModal, setShowModal] = useState<boolean>(false);

  // Filter lost leads
  const lostLeads = allLeads.filter((l) => {
    const st = (l.lead_status || "").toLowerCase();
    return ["sql lost", "sal lost", "lost lead", "lead lost", "not interested"].some((s) => st.includes(s));
  });

  if (lostLeads.length === 0) {
    return (
      <div className="mt-6 pt-4 border-t border-slate-200">
        <div className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-2 flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-rose-600"></span>
          SQL Lost & Lead Lost Audit
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-card p-4 text-xs text-slate-500 italic text-center">
          No SQL Lost or Lead Lost records for this period.
        </div>
      </div>
    );
  }

  // Categorize lost leads
  const sqlLostLeads = lostLeads.filter((l) => {
    const st = (l.lead_status || "").toLowerCase();
    return st.includes("sql lost") || matchesStage(l, "SQL");
  });

  const leadLostLeads = lostLeads.filter((l) => {
    const st = (l.lead_status || "").toLowerCase();
    return st.includes("lost lead") || st.includes("lead lost") || st.includes("sal lost");
  });

  const notInterestedLeads = lostLeads.filter((l) => {
    const st = (l.lead_status || "").toLowerCase();
    return st.includes("not interested");
  });

  const categoryLeadsMap: Record<string, SourceLead[]> = {
    All: lostLeads,
    "SQL Lost": sqlLostLeads,
    "Lead Lost": leadLostLeads,
    "Not Interested": notInterestedLeads,
  };

  const activeList = categoryLeadsMap[activeCategory] || lostLeads;

  return (
    <div className="mt-6 pt-4 border-t border-slate-200 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-rose-600"></span>
            SQL Lost & Lead Lost Audit
          </h4>
          <p className="text-xs text-slate-500 mt-0.5">
            Click category cards to filter & inspect loss reasons & Fathom call takeaways
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowModal(true)}
          className="text-xs font-semibold px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white rounded-md transition-colors shadow-xs flex items-center gap-1.5 cursor-pointer"
        >
          <span>🔍 Expand All Lost ({activeList.length})</span>
        </button>
      </div>

      {/* Interactive Category Filter Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <button
          type="button"
          onClick={() => setActiveCategory("All")}
          className={cn(
            "p-3 rounded-card text-left border transition-all cursor-pointer",
            activeCategory === "All"
              ? "bg-slate-900 text-white border-slate-900 shadow-md ring-2 ring-slate-900/20"
              : "bg-slate-50 hover:bg-slate-100 border-slate-200 text-slate-800"
          )}
        >
          <div className={cn("text-[10px] font-semibold uppercase tracking-wider", activeCategory === "All" ? "text-slate-300" : "text-slate-500")}>
            Total Lost Records
          </div>
          <div className="text-xl font-bold mt-0.5">{lostLeads.length}</div>
          <div className={cn("text-[11px] mt-0.5", activeCategory === "All" ? "text-slate-300" : "text-slate-500")}>All lost stages combined</div>
        </button>

        <button
          type="button"
          onClick={() => setActiveCategory("SQL Lost")}
          className={cn(
            "p-3 rounded-card text-left border transition-all cursor-pointer",
            activeCategory === "SQL Lost"
              ? "bg-rose-600 text-white border-rose-600 shadow-md ring-2 ring-rose-600/20"
              : "bg-rose-50/70 hover:bg-rose-100/70 border-rose-200 text-rose-950"
          )}
        >
          <div className={cn("text-[10px] font-semibold uppercase tracking-wider", activeCategory === "SQL Lost" ? "text-rose-100" : "text-rose-800")}>
            SQL Lost
          </div>
          <div className="text-xl font-bold mt-0.5">{sqlLostLeads.length}</div>
          <div className={cn("text-[11px] mt-0.5", activeCategory === "SQL Lost" ? "text-rose-100" : "text-rose-700")}>Post-qualification drop-offs</div>
        </button>

        <button
          type="button"
          onClick={() => setActiveCategory("Lead Lost")}
          className={cn(
            "p-3 rounded-card text-left border transition-all cursor-pointer",
            activeCategory === "Lead Lost"
              ? "bg-amber-600 text-white border-amber-600 shadow-md ring-2 ring-amber-600/20"
              : "bg-amber-50/70 hover:bg-amber-100/70 border-amber-200 text-amber-950"
          )}
        >
          <div className={cn("text-[10px] font-semibold uppercase tracking-wider", activeCategory === "Lead Lost" ? "text-amber-100" : "text-amber-800")}>
            Lead / SAL Lost
          </div>
          <div className="text-xl font-bold mt-0.5">{leadLostLeads.length}</div>
          <div className={cn("text-[11px] mt-0.5", activeCategory === "Lead Lost" ? "text-amber-100" : "text-amber-700")}>Pre-demo / early drop-offs</div>
        </button>

        <button
          type="button"
          onClick={() => setActiveCategory("Not Interested")}
          className={cn(
            "p-3 rounded-card text-left border transition-all cursor-pointer",
            activeCategory === "Not Interested"
              ? "bg-purple-600 text-white border-purple-600 shadow-md ring-2 ring-purple-600/20"
              : "bg-purple-50/70 hover:bg-purple-100/70 border-purple-200 text-purple-950"
          )}
        >
          <div className={cn("text-[10px] font-semibold uppercase tracking-wider", activeCategory === "Not Interested" ? "text-purple-100" : "text-purple-800")}>
            Not Interested
          </div>
          <div className="text-xl font-bold mt-0.5">{notInterestedLeads.length}</div>
          <div className={cn("text-[11px] mt-0.5", activeCategory === "Not Interested" ? "text-purple-100" : "text-purple-700")}>Explicit rejections</div>
        </button>
      </div>

      {/* Filtered 6-Card Preview Grid */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-rose-600"></span>
            <span>Filter: <strong>{activeCategory} Records</strong> ({activeList.length} total)</span>
          </h4>
          {activeList.length > 6 && (
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="text-xs text-rose-600 hover:text-rose-800 font-bold hover:underline cursor-pointer"
            >
              View all {activeList.length} records in modal →
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {activeList.slice(0, 6).map((lead) => {
            const leadIdStr = String(lead.id || "");
            const emailKey = (lead.email || "").toLowerCase().trim();
            const nameKey = (lead.name || "").toLowerCase().trim();

            const fathomMatch = qcResults.find(
              (q) =>
                (emailKey && (q.email || "").toLowerCase().trim() === emailKey) ||
                String(q.lead_id) === leadIdStr ||
                (nameKey && (q.lead_name || "").toLowerCase().trim() === nameKey)
            );

            let rawCrmReason = (lead as any).reason;
            if (!rawCrmReason && lead.description && !lead.description.includes("what's_your_pos_system") && !lead.description.includes("nav_ref=") && !lead.description.includes("inbox_url=")) {
              rawCrmReason = lead.description;
            }
            const crmReason = rawCrmReason || lead.clay_lead_summary || null;

            // Filter out system call logs, email templates, sales text outreach copy, and strip HTML tags
            const cleanCrmNotes = ((lead as any).crm_notes_list || [])
              .map((n: string) => n.replace(/<[^>]*>?/gm, "").trim())
              .filter((n: string) => {
                const low = n.toLowerCase();
                return (
                  !low.includes("call transcript -") &&
                  !low.includes("call summary -") &&
                  !low.includes("[voicemail]") &&
                  !low.includes("email sent:") &&
                  !low.includes("day 01 email") &&
                  !low.includes("day 1 email") &&
                  !low.includes("thank you for your interest") &&
                  !low.includes("applova kiosks help restaurants") &&
                  !low.includes("follow up cadence:") &&
                  !low.includes("applova website")
                );
              });

            const fathomTakeaway = fathomMatch?.key_takeaways?.[0] || fathomMatch?.recommended_action || null;
            const isHighQuality = fathomMatch && ((fathomMatch.overall_quality_score || 0) >= 8 || fathomMatch.quality_band === "High");

            return (
              <div
                key={lead.id || lead.email || lead.name}
                className={cn(
                  "border rounded-card p-3 text-xs flex flex-col justify-between transition-shadow hover:shadow-sm space-y-2",
                  isHighQuality ? "bg-rose-50/50 border-rose-200" : "bg-white border-slate-200"
                )}
              >
                <div>
                  <div className="flex items-center justify-between font-bold text-slate-900 mb-1">
                    <span className="truncate max-w-[160px]" title={lead.name}>
                      {lead.name}
                    </span>
                    <div className="flex items-center space-x-1 shrink-0">
                      <span className="bg-rose-100 text-rose-800 font-bold px-1.5 py-0.5 rounded text-[10px] border border-rose-200">
                        {lead.lead_status || "Lost"}
                      </span>
                      {fathomMatch && (
                        <span className={cn("font-bold px-1.5 py-0.5 rounded text-[10px]", (fathomMatch.overall_quality_score || 0) >= 8 ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800")}>
                          QC: {fathomMatch.overall_quality_score}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="text-slate-500 text-[11px] mb-2 truncate" title={lead.company || "N/A"}>
                    {lead.company || "N/A"} • Rep: <strong className="text-slate-700">{lead.owner || "Unassigned"}</strong>
                  </div>

                  <div className="bg-slate-50 border border-slate-200/70 rounded p-2 text-[11px] space-y-1">
                    {cleanCrmNotes.length > 0 ? (
                      <div>
                        <strong className="text-slate-900 font-bold">CRM Rep Note:</strong>{" "}
                        <span className="text-slate-800">{cleanCrmNotes[0].length > 100 ? `${cleanCrmNotes[0].slice(0, 100)}...` : cleanCrmNotes[0]}</span>
                        <a 
                          href={`https://crm.zoho.com/crm/EntityInfo.do?module=${lead.module || ("Lead_Status" in lead ? "Leads" : ("Contact_Name" in lead ? "Contacts" : "Deals"))}&id=${lead.id || ""}`}
                          target="_blank" 
                          rel="noreferrer"
                          className="text-[10px] text-blue-600 hover:text-blue-800 font-semibold inline-flex items-center gap-1 ml-2 transition-colors"
                        >
                          View in CRM 
                          <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg>
                        </a>
                      </div>
                    ) : crmReason ? (
                      <div>
                        <strong className="text-slate-900 font-bold">CRM Reason:</strong>{" "}
                        <span className="text-slate-800">{crmReason}</span>
                      </div>
                    ) : null}

                    {fathomTakeaway && (
                      <div className={cn(cleanCrmNotes.length > 0 || crmReason ? "pt-1 border-t border-slate-200/60" : "")}>
                        <strong className="text-rose-900">Call Takeaway:</strong>{" "}
                        <span className="text-slate-800 font-medium">{fathomTakeaway}</span>
                      </div>
                    )}

                    {!cleanCrmNotes.length && !crmReason && !fathomTakeaway && (
                      <div className="text-slate-500 italic">
                        {lead.lead_status} (No specific loss reason logged in CRM)
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-2.5 pt-2 border-t border-slate-100 flex items-center justify-between text-[11px]">
                  <span className="text-slate-400">
                    Stage: <strong className="text-slate-700">{lead.stage || (matchesStage(lead, "SQL") ? "SQL" : "MQL")}</strong>
                  </span>
                  <div className="flex items-center space-x-2">
                    {fathomMatch?.fathom_recording_url && (
                      <a href={fathomMatch.fathom_recording_url} target="_blank" rel="noreferrer" className="text-blue-600 font-bold hover:underline flex items-center gap-0.5">
                        🎥 Call →
                      </a>
                    )}
                    {lead.zoho_url && (
                      <a href={lead.zoho_url} target="_blank" rel="noreferrer" className="text-slate-600 font-medium hover:underline">
                        CRM →
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {activeList.length > 6 && (
          <div className="mt-3 text-center">
            <button
              type="button"
              onClick={() => setShowModal(true)}
              className="inline-flex items-center space-x-1.5 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-md text-xs font-semibold transition-colors cursor-pointer"
            >
              <span>Showing 6 of {activeList.length} {activeCategory} records. Click to expand full list</span>
            </button>
          </div>
        )}
      </div>

      {/* Expanded Modal Dialog */}
      {showModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-card shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-border bg-slate-50 shrink-0">
              <div>
                <h3 className="text-base font-bold text-slate-900">
                  SQL Lost & Lead Lost Audit — {activeCategory} ({activeList.length} Records)
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  Detailed loss reasons, rep notes, and Fathom AI call takeaways from Zoho CRM
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="px-3 py-1 bg-slate-200 hover:bg-slate-300 rounded text-xs font-bold text-slate-700 cursor-pointer"
              >
                Close ✕
              </button>
            </div>

            <div className="p-5 overflow-y-auto space-y-3 flex-1">
              {activeList.map((lead) => {
                const leadIdStr = String(lead.id || "");
                const emailKey = (lead.email || "").toLowerCase().trim();
                const nameKey = (lead.name || "").toLowerCase().trim();

                const fathomMatch = qcResults.find(
                  (q) =>
                    (emailKey && (q.email || "").toLowerCase().trim() === emailKey) ||
                    String(q.lead_id) === leadIdStr ||
                    (nameKey && (q.lead_name || "").toLowerCase().trim() === nameKey)
                );

                let rawCrmReason = (lead as any).reason;
                if (!rawCrmReason && lead.description && !lead.description.includes("what's_your_pos_system") && !lead.description.includes("nav_ref=") && !lead.description.includes("inbox_url=")) {
                  rawCrmReason = lead.description;
                }
                const crmReason = rawCrmReason || lead.clay_lead_summary || null;

                const cleanCrmNotes = ((lead as any).crm_notes_list || [])
                  .map((n: string) => n.replace(/<[^>]*>?/gm, "").trim())
                  .filter((n: string) => {
                    const low = n.toLowerCase();
                    return (
                      !low.includes("call transcript -") &&
                      !low.includes("call summary -") &&
                      !low.includes("[voicemail]") &&
                      !low.includes("email sent:") &&
                      !low.includes("day 01 email") &&
                      !low.includes("day 1 email") &&
                      !low.includes("thank you for your interest") &&
                      !low.includes("applova kiosks help restaurants") &&
                      !low.includes("follow up cadence:") &&
                      !low.includes("applova website")
                    );
                  });

                const fathomTakeaways = fathomMatch?.key_takeaways || [];
                const fathomAction = fathomMatch?.recommended_action;

                return (
                  <div key={lead.id || lead.email || lead.name} className="border border-slate-200 rounded-card p-3 text-xs bg-white flex flex-col justify-between space-y-2">
                    <div className="flex items-center justify-between font-bold text-slate-900">
                      <span>{lead.name} ({lead.company || "N/A"}) — <span className="text-slate-600 font-normal">Rep: {lead.owner || "Unassigned"}</span></span>
                      <div className="flex items-center space-x-2">
                        <span className="bg-rose-100 text-rose-800 font-bold px-2 py-0.5 rounded text-[10px] border border-rose-200">
                          {lead.lead_status || "Lost"}
                        </span>
                        {fathomMatch && (
                          <span className={cn("font-bold px-2 py-0.5 rounded text-[10px]", (fathomMatch.overall_quality_score || 0) >= 8 ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800")}>
                            QC Score: {fathomMatch.overall_quality_score}/10
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="bg-slate-50 border border-slate-200 rounded p-2.5 text-[11px] space-y-1.5">
                      {cleanCrmNotes.length > 0 && (
                        <div>
                          <strong className="text-slate-900 font-bold">Zoho CRM Rep Notes:</strong>
                          <span className="text-slate-800 ml-1">{cleanCrmNotes[0].length > 100 ? `${cleanCrmNotes[0].slice(0, 100)}...` : cleanCrmNotes[0]}</span>
                          <a 
                            href={`https://crm.zoho.com/crm/EntityInfo.do?module=${lead.module || ("Lead_Status" in lead ? "Leads" : ("Contact_Name" in lead ? "Contacts" : "Deals"))}&id=${lead.id || ""}`}
                            target="_blank" 
                            rel="noreferrer"
                            className="text-[10px] text-blue-600 hover:text-blue-800 font-semibold inline-flex items-center gap-1 ml-2 transition-colors"
                          >
                            View in CRM 
                            <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg>
                          </a>
                        </div>
                      )}
                      {crmReason && !cleanCrmNotes.length && (
                        <div>
                          <strong className="text-slate-900 font-bold">Zoho CRM Loss Reason:</strong>{" "}
                          <span className="text-slate-700">{crmReason}</span>
                        </div>
                      )}
                      {fathomTakeaways.length > 0 && (
                        <div className={cn(cleanCrmNotes.length > 0 || crmReason ? "pt-1 border-t border-slate-200/60" : "")}>
                          <strong className="text-rose-900 font-bold">Fathom Call Key Takeaways:</strong>
                          <ul className="list-disc pl-4 text-slate-800 space-y-0.5 mt-0.5">
                            {fathomTakeaways.map((t, idx) => (
                              <li key={idx}>{t}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {fathomAction && (
                        <div className={cn(cleanCrmNotes.length > 0 || crmReason || fathomTakeaways.length > 0 ? "pt-1 border-t border-slate-200/60" : "")}>
                          <strong className="text-blue-900 font-bold">Recommended Follow-up:</strong>{" "}
                          <span className="text-slate-800">{fathomAction}</span>
                        </div>
                      )}
                      {!cleanCrmNotes.length && !crmReason && !fathomTakeaways.length && !fathomAction && (
                        <div className="text-slate-500 italic">
                          Status: {lead.lead_status} (No specific call takeaway or rep loss note logged)
                        </div>
                      )}
                    </div>

                    <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1 border-t border-slate-100">
                      <span>Stage: <strong className="text-slate-700">{lead.stage || (matchesStage(lead, "SQL") ? "SQL" : "MQL")}</strong></span>
                      <div className="flex items-center space-x-3">
                        {fathomMatch?.fathom_recording_url && (
                          <a href={fathomMatch.fathom_recording_url} target="_blank" rel="noreferrer" className="text-blue-600 font-bold hover:underline flex items-center gap-1">
                            🎥 Fathom Call Audit →
                          </a>
                        )}
                        {lead.zoho_url && (
                          <a href={lead.zoho_url} target="_blank" rel="noreferrer" className="text-slate-600 font-medium hover:underline">
                            Zoho CRM →
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
